import React from 'react';
import { motion, HTMLMotionProps } from 'motion/react';
import { cn } from '../../lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'link' | 'icon';
export type ButtonSize = 'small' | 'medium' | 'large' | 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  fullWidth?: boolean;
  pill?: boolean;
  children?: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs hover:shadow active:bg-indigo-800 border-transparent',
  secondary: 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100 border-transparent',
  outline: 'bg-transparent border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 shadow-2xs',
  ghost: 'bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 border-transparent',
  danger: 'bg-rose-600 hover:bg-rose-700 text-white shadow-xs hover:shadow active:bg-rose-800 border-transparent',
  link: 'bg-transparent text-indigo-600 hover:text-indigo-700 hover:underline p-0 border-transparent shadow-none',
  icon: 'bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 border-transparent rounded-xl',
};

/**
 * Standardized button sizing across the entire application.
 * Default is 'medium' (h-8 = 32px), matching the "New Society" button standard on Societies Management page.
 */
const sizeStyles: Record<ButtonSize, string> = {
  small: 'h-7 px-2.5 text-[11px] gap-1 font-semibold rounded-lg',
  sm: 'h-7 px-2.5 text-[11px] gap-1 font-semibold rounded-lg',
  medium: 'h-8 px-3 text-xs gap-1.5 font-bold rounded-xl',
  md: 'h-8 px-3 text-xs gap-1.5 font-bold rounded-xl',
  large: 'h-9 px-4 text-xs sm:text-sm gap-2 font-bold rounded-xl',
  lg: 'h-9 px-4 text-xs sm:text-sm gap-2 font-bold rounded-xl',
  icon: 'h-8 w-8 p-0 text-xs gap-0 rounded-xl flex items-center justify-center',
};

/**
 * Enterprise Design System Button
 * 
 * Standard height default: h-8 (32px), matching "New Society" button.
 * 
 * Usage:
 * ```tsx
 * <Button variant="primary">Save</Button>
 * <Button variant="outline" size="sm">Cancel</Button>
 * ```
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  icon,
  iconPosition = 'left',
  loading = false,
  fullWidth = false,
  pill = false,
  className,
  children,
  disabled,
  ...props
}) => {
  return (
    <motion.button
      whileHover={{ y: -1, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className={cn(
        'inline-flex items-center justify-center font-bold border transition-all select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 shrink-0 cursor-pointer',
        pill ? 'rounded-full' : 'rounded-xl',
        variantStyles[variant],
        sizeStyles[size],
        fullWidth && 'w-full',
        (disabled || loading) && 'opacity-60 cursor-not-allowed pointer-events-none',
        className
      )}
      disabled={loading || disabled}
      {...props}
    >
      {loading ? (
        <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin shrink-0" />
      ) : icon && iconPosition === 'left' ? (
        <span className="shrink-0 flex items-center justify-center [&>svg]:w-3.5 [&>svg]:h-3.5">{icon}</span>
      ) : null}
      {children}
      {!loading && icon && iconPosition === 'right' ? (
        <span className="shrink-0 flex items-center justify-center [&>svg]:w-3.5 [&>svg]:h-3.5">{icon}</span>
      ) : null}
    </motion.button>
  );
};

export default Button;
